/**
 * ImportDialog Component
 * 
 * Dialog para importação de lançamentos com preview e validação
 */

import { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import { Progress } from '../../../components/ui/progress';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import {
  Upload,
  FileJson,
  AlertCircle,
  CheckCircle2,
  Info,
  AlertTriangle,
  FileSpreadsheet,
} from 'lucide-react';
import { useImportTransactions } from '../hooks/useImportTransactions';
import { importTransactionsFromExcel, type ImportExcelResult } from '../services/import-excel.service';
import type { ImportOptions } from '../../../types/export-import';
import { cn } from '../../../components/ui/utils';
import { toast } from 'sonner';

interface ImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  userId: string;
}

type ImportStep = 'upload' | 'preview' | 'importing' | 'complete';

export function ImportDialog({ open, onOpenChange, companyId, userId }: ImportDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<ImportStep>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [format, setFormat] = useState<'json' | 'excel'>('excel');
  const [excelResult, setExcelResult] = useState<ImportExcelResult | null>(null);
  const [isImportingExcel, setIsImportingExcel] = useState(false);

  const [options, setOptions] = useState<ImportOptions>({
    skip_duplicates: true,
    auto_create_missing: true,
    update_existing: false,
    import_master_data: true,
  });

  const {
    validateFile,
    isValidatingFile,
    validatedData,
    validateTransactions,
    isValidatingTransactions,
    validationResult,
    importTransactions,
    isImporting,
    importResult,
    importProgress,
    reset,
  } = useImportTransactions({ companyId, userId });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      
      // Detect format from file extension
      const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
      const detectedFormat = isExcel ? 'excel' : 'json';
      setFormat(detectedFormat);
      
      // Only validate JSON files (Excel has different flow)
      if (!isExcel) {
        validateFile(file);
      }
      // For Excel: stay on upload step, user will click button to proceed
    }
  };

  const handleNext = () => {
    if (step === 'upload') {
      if (format === 'excel' && selectedFile) {
        // Excel: go to preview/confirmation step
        setStep('preview');
      } else if (validatedData) {
        // JSON: validate transactions
      validateTransactions({ data: validatedData, options });
      setStep('preview');
      }
    } else if (step === 'preview') {
      if (format === 'excel' && selectedFile) {
        // Import Excel
        handleImportExcel();
      } else if (validationResult?.is_valid && validatedData) {
        // Iniciar importação JSON
        setStep('importing');
        importTransactions({ data: validatedData, options });
      }
    }
  };

  const [excelProgress, setExcelProgress] = useState({
    current: 0,
    total: 0,
    message: '',
  });

  const handleImportExcel = async () => {
    if (!selectedFile) return;
    
    setStep('importing');
    setIsImportingExcel(true);
    setExcelProgress({ current: 0, total: 0, message: 'Iniciando importação...' });
    
    try {
      const result = await importTransactionsFromExcel(
        selectedFile,
        userId,
        companyId,
        {
          skipDuplicates: options.skip_duplicates,
          autoCreateMasterData: options.auto_create_missing,
        },
        // Progress callback
        (progress) => {
          setExcelProgress(progress);
        }
      );
      
      setExcelResult(result);
      setStep('complete');
      
      if (result.errors === 0) {
        toast.success(`${result.success} lançamentos importados com sucesso!`);
      } else {
        toast.warning(`Importação concluída: ${result.success} sucesso, ${result.errors} erros`);
      }
    } catch (error) {
      console.error('[ImportDialog] Excel import error:', error);
      toast.error('Erro ao importar arquivo Excel');
      setStep('preview');
    } finally {
      setIsImportingExcel(false);
      setExcelProgress({ current: 0, total: 0, message: '' });
    }
  };

  const handleCancel = () => {
    reset();
    setStep('upload');
    setSelectedFile(null);
    setExcelResult(null);
    setExcelProgress({ current: 0, total: 0, message: '' });
    setFormat('excel');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    handleCancel();
    onOpenChange(false);
  };

  // Atualizar step quando importação terminar
  if (step === 'importing' && importResult && !isImporting) {
    setStep('complete');
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Importar Lançamentos
          </DialogTitle>
          <DialogDescription>
            {step === 'upload' && 'Selecione o formato e faça upload do arquivo'}
            {step === 'preview' && 'Revise os dados antes de importar'}
            {step === 'importing' && 'Importando lançamentos...'}
            {step === 'complete' && 'Importação concluída!'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div className="space-y-4">
              {/* Format Selection */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Formato do arquivo</Label>
                <RadioGroup value={format} onValueChange={(value) => setFormat(value as 'json' | 'excel')}>
                  <div className="grid grid-cols-2 gap-3">
                    <label
                      className={cn(
                        'flex items-center space-x-3 border rounded-lg p-4 cursor-pointer transition-colors',
                        format === 'excel' 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-primary/50'
                      )}
                    >
                      <RadioGroupItem value="excel" id="format-excel" />
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet className="w-5 h-5 text-green-600" />
                        <div>
                          <div className="font-medium">Excel</div>
                          <div className="text-xs text-muted-foreground">.xlsx</div>
                        </div>
                      </div>
                    </label>
                    <label
                      className={cn(
                        'flex items-center space-x-3 border rounded-lg p-4 cursor-pointer transition-colors',
                        format === 'json' 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-primary/50'
                      )}
                    >
                      <RadioGroupItem value="json" id="format-json" />
                      <div className="flex items-center gap-2">
                        <FileJson className="w-5 h-5 text-blue-600" />
                        <div>
                          <div className="font-medium">JSON</div>
                          <div className="text-xs text-muted-foreground">.json</div>
                        </div>
                      </div>
                    </label>
                  </div>
                </RadioGroup>
              </div>

              {/* File Input */}
              <div
                className={cn(
                  'border-2 border-dashed rounded-lg p-8 text-center transition-colors',
                  selectedFile ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
                )}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={format === 'excel' ? '.xlsx,.xls' : '.json'}
                  onChange={handleFileSelect}
                  className="hidden"
                  id="import-file"
                />
                <label
                  htmlFor="import-file"
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  {format === 'excel' ? (
                    <FileSpreadsheet className="h-12 w-12 text-green-600" />
                  ) : (
                    <FileJson className="h-12 w-12 text-blue-600" />
                  )}
                  <div>
                    <p className="text-sm font-medium">
                      {selectedFile ? selectedFile.name : 'Clique para selecionar arquivo'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {format === 'excel' ? 'Arquivo Excel (.xlsx)' : 'Arquivo JSON (até 10MB)'}
                    </p>
                  </div>
                </label>
              </div>

              {/* Validação do arquivo */}
              {isValidatingFile && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>Validando arquivo...</AlertDescription>
                </Alert>
              )}

              {/* File selected (Excel) */}
              {format === 'excel' && selectedFile && (
                <Alert className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
                  <FileSpreadsheet className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <AlertDescription className="text-blue-800 dark:text-blue-300">
                    Arquivo Excel selecionado! Clique em "Avançar" para revisar antes de importar.
                  </AlertDescription>
                </Alert>
              )}

              {/* File validated (JSON) */}
              {format === 'json' && validatedData && (
                <Alert className="bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800">
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <AlertDescription className="text-green-800 dark:text-green-300">
                    Arquivo válido! {validatedData.transactions.length} lançamentos encontrados.
                  </AlertDescription>
                </Alert>
              )}

              {/* Opções de importação (only for JSON) */}
              {format === 'json' && validatedData && (
                <div className="space-y-3 pt-2">
                  <Label className="text-base font-semibold">Opções de Importação</Label>

                  <div className="space-y-3">
                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="skip_duplicates"
                        checked={options.skip_duplicates}
                        onCheckedChange={(checked) =>
                          setOptions((prev) => ({ ...prev, skip_duplicates: checked === true }))
                        }
                      />
                      <div className="grid gap-1.5 leading-none">
                        <label
                          htmlFor="skip_duplicates"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Pular duplicatas
                        </label>
                        <p className="text-xs text-muted-foreground">
                          Ignora lançamentos duplicados (compara: descrição, valor, datas de lançamento/vencimento/pagamento, categoria, subcategoria, centro de custo, método de pagamento e parceiro)
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="auto_create_missing"
                        checked={options.auto_create_missing}
                        onCheckedChange={(checked) =>
                          setOptions((prev) => ({ ...prev, auto_create_missing: checked === true }))
                        }
                      />
                      <div className="grid gap-1.5 leading-none">
                        <label
                          htmlFor="auto_create_missing"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Criar dados faltantes automaticamente
                        </label>
                        <p className="text-xs text-muted-foreground">
                          Criar categorias, métodos de pagamento, etc. que não existem
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="import_master_data"
                        checked={options.import_master_data}
                        onCheckedChange={(checked) =>
                          setOptions((prev) => ({ ...prev, import_master_data: checked === true }))
                        }
                      />
                      <div className="grid gap-1.5 leading-none">
                        <label
                          htmlFor="import_master_data"
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          Importar dados mestres
                        </label>
                        <p className="text-xs text-muted-foreground">
                          Importar categorias, métodos de pagamento do arquivo
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && (
            <div className="space-y-4">
              {/* Excel Preview */}
              {format === 'excel' && selectedFile && (
                <>
                  <Alert>
                    <FileSpreadsheet className="h-4 w-4" />
                    <AlertDescription>
                      <p className="font-semibold mb-2">Arquivo selecionado:</p>
                      <p className="text-sm">{selectedFile.name}</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        O arquivo será processado e os lançamentos serão importados. Configure as opções abaixo antes de continuar.
                      </p>
                    </AlertDescription>
                  </Alert>

                  {/* Opções de importação para Excel */}
                  <div className="space-y-3 pt-2">
                    <Label className="text-base font-semibold">Opções de Importação</Label>

                    <div className="space-y-3">
                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="skip_duplicates_excel"
                          checked={options.skip_duplicates}
                          onCheckedChange={(checked) =>
                            setOptions((prev) => ({ ...prev, skip_duplicates: checked === true }))
                          }
                        />
                        <div className="grid gap-1.5 leading-none">
                          <label
                            htmlFor="skip_duplicates_excel"
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                          >
                            Pular duplicatas
                          </label>
                          <p className="text-xs text-muted-foreground">
                            Ignora lançamentos duplicados (compara: descrição, valor, datas de lançamento/vencimento/pagamento, categoria, subcategoria, centro de custo, método de pagamento e parceiro)
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="auto_create_missing_excel"
                          checked={options.auto_create_missing}
                          onCheckedChange={(checked) =>
                            setOptions((prev) => ({ ...prev, auto_create_missing: checked === true }))
                          }
                        />
                        <div className="grid gap-1.5 leading-none">
                          <label
                            htmlFor="auto_create_missing_excel"
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                          >
                            Criar dados faltantes automaticamente
                          </label>
                          <p className="text-xs text-muted-foreground">
                            Criar categorias, métodos de pagamento, etc. que não existem
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* JSON Preview */}
              {isValidatingTransactions && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>Validando transações...</AlertDescription>
                </Alert>
              )}

              {validationResult && (
                <div className="space-y-4">
                  {/* Resultado da validação */}
                  {validationResult.is_valid ? (
                    <Alert className="bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800">
                      <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                      <AlertDescription className="text-green-800 dark:text-green-300">
                        <p className="font-semibold mb-2">Validação concluída com sucesso!</p>
                        <div className="text-sm space-y-1">
                          <p>{validationResult.stats.valid_transactions} lançamentos válidos</p>
                          {validationResult.stats.duplicates_found > 0 && (
                            <p>
                              AVISO: {validationResult.stats.duplicates_found} duplicatas serão
                              ignoradas
                            </p>
                          )}
                        </div>
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <p className="font-semibold mb-2">Erros encontrados na validação</p>
                        <div className="text-sm space-y-1">
                          <p>ERRO: {validationResult.stats.invalid_transactions} lançamentos inválidos</p>
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Dados mestres faltantes */}
                  {validationResult.stats.missing_master_data && (
                    <div className="rounded-lg border p-4 space-y-2">
                      <p className="text-sm font-medium">Dados que serão criados:</p>
                      <div className="text-xs text-muted-foreground space-y-1">
                        {validationResult.stats.missing_master_data.categories.length > 0 && (
                          <p>
                            • {validationResult.stats.missing_master_data.categories.length}{' '}
                            categorias
                          </p>
                        )}
                        {validationResult.stats.missing_master_data.payment_methods.length > 0 && (
                          <p>
                            • {validationResult.stats.missing_master_data.payment_methods.length}{' '}
                            métodos de pagamento
                          </p>
                        )}
                        {validationResult.stats.missing_master_data.business_partners.length > 0 && (
                          <p>
                            • {validationResult.stats.missing_master_data.business_partners.length}{' '}
                            parceiros comerciais
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Erros detalhados */}
                  {validationResult.errors.length > 0 && (
                    <div className="max-h-48 overflow-y-auto rounded-lg border p-4 space-y-2">
                      <p className="text-sm font-medium text-destructive">Erros:</p>
                      {validationResult.errors.slice(0, 10).map((error, index) => (
                        <div key={index} className="text-xs text-muted-foreground">
                          <span className="font-mono">Linha {error.line}:</span> {error.message}
                        </div>
                      ))}
                      {validationResult.errors.length > 10 && (
                        <p className="text-xs text-muted-foreground italic">
                          ... e mais {validationResult.errors.length - 10} erros
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Importing */}
          {step === 'importing' && (
            <div className="space-y-4">
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Importando lançamentos... Não feche esta janela.
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                {/* Use excelProgress if importing Excel, otherwise use importProgress */}
                {format === 'excel' ? (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">{excelProgress.message}</span>
                      <span className="text-muted-foreground">
                        {excelProgress.current} / {excelProgress.total}
                      </span>
                    </div>
                    <Progress
                      value={
                        excelProgress.total > 0
                          ? (excelProgress.current / excelProgress.total) * 100
                          : 0
                      }
                    />
                    {excelProgress.total > 0 && (
                      <p className="text-xs text-muted-foreground text-center">
                        {Math.round((excelProgress.current / excelProgress.total) * 100)}% concluído
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="font-medium">{importProgress.message}</span>
                      <span className="text-muted-foreground">
                        {importProgress.current} / {importProgress.total}
                      </span>
                    </div>
                    <Progress
                      value={
                        importProgress.total > 0
                          ? (importProgress.current / importProgress.total) * 100
                          : 0
                      }
                    />
                    {importProgress.total > 0 && (
                      <p className="text-xs text-muted-foreground text-center">
                        {Math.round((importProgress.current / importProgress.total) * 100)}% concluído
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Complete */}
          {step === 'complete' && (format === 'excel' ? excelResult : importResult) && (
            <div className="space-y-4">
              {format === 'excel' && excelResult ? (
                // Excel Results
                <>
                  <Alert className="bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800">
                    <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <AlertDescription className="text-green-800 dark:text-green-300">
                      <p className="font-semibold mb-2">Importação concluída!</p>
                      <div className="text-sm space-y-1">
                        <p>{excelResult.success} lançamentos importados</p>
                        {excelResult.skipped > 0 && <p>{excelResult.skipped} duplicados ignorados</p>}
                        {excelResult.errors > 0 && <p className="text-red-600">{excelResult.errors} erros</p>}
                      </div>
                    </AlertDescription>
                  </Alert>

                  {excelResult.details.length > 0 && (
                    <div className="max-h-96 overflow-y-auto rounded-lg border p-4 space-y-2">
                      <p className="text-sm font-medium">Detalhes da importação:</p>
                      {excelResult.details.map((detail, index) => (
                        <div key={index} className="text-xs flex items-start gap-2 py-1">
                          <span className="font-mono text-muted-foreground">L{detail.row}:</span>
                          <span className="flex-1">{detail.description}</span>
                          <span className={cn(
                            'font-medium',
                            detail.status === 'success' && 'text-green-600',
                            detail.status === 'skipped' && 'text-yellow-600',
                            detail.status === 'error' && 'text-red-600'
                          )}>
                            {detail.status === 'success' && 'OK'}
                            {detail.status === 'skipped' && 'SKIP'}
                            {detail.status === 'error' && 'ERRO'}
                            {detail.message && ` ${detail.message}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : importResult?.success ? (
                // JSON Results
                <Alert className="bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800">
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <AlertDescription className="text-green-800 dark:text-green-300">
                    <p className="font-semibold mb-2">Importação concluída com sucesso!</p>
                    <div className="text-sm space-y-1">
                      <p>SUCESSO: {importResult?.imported || 0} lançamentos importados</p>
                      {(importResult?.skipped || 0) > 0 && <p>AVISO: {importResult.skipped} ignorados (duplicatas)</p>}
                      {(importResult?.errors || 0) > 0 && <p>ERRO: {importResult.errors} com erro</p>}
                    </div>
                  </AlertDescription>
                </Alert>
              ) : importResult ? (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <p className="font-semibold mb-2">Importação concluída com erros</p>
                    <div className="text-sm space-y-1">
                      <p>SUCESSO: {importResult?.imported || 0} importados</p>
                      <p>ERRO: {importResult?.errors || 0} com erro</p>
                      <p>AVISO: {importResult?.skipped || 0} ignorados</p>
                    </div>
                  </AlertDescription>
                </Alert>
              ) : null}

              {/* Dados mestres criados (only for JSON imports) */}
              {format === 'json' && importResult?.created_master_data && (
                <div className="rounded-lg border p-4 space-y-2">
                  <p className="text-sm font-medium">Dados criados:</p>
                  <div className="text-xs text-muted-foreground space-y-1">
                    {importResult.created_master_data.categories > 0 && (
                      <p>• {importResult.created_master_data.categories} categorias</p>
                    )}
                    {importResult.created_master_data.payment_methods > 0 && (
                      <p>• {importResult.created_master_data.payment_methods} métodos de pagamento</p>
                    )}
                    {importResult.created_master_data.business_partners > 0 && (
                      <p>• {importResult.created_master_data.business_partners} parceiros</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {step === 'complete' ? (
            <Button onClick={handleClose}>Fechar</Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isImporting || isValidatingFile || isValidatingTransactions}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleNext}
                disabled={
                  (format === 'excel' && !selectedFile) ||
                  (format === 'json' && !validatedData) ||
                  isImporting ||
                  isValidatingFile ||
                  isValidatingTransactions ||
                  (step === 'preview' && format === 'json' && !validationResult?.is_valid)
                }
              >
                {step === 'upload' && 'Avançar'}
                {step === 'preview' && 'Importar'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

